'use client';

import * as React from 'react';
import { MoreVertical, Tag, FolderInput, ArrowRightLeft } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { TransferDialog } from '@/features/workspaces';
import { useScopesStore } from './scopesStore';
import { useDocumentsStore } from './documentsStore';
import { TagEditorDialog } from './TagEditorDialog';
import { computeMovedTags, slugifyTag } from './scopeTags';

interface DocumentActionsMenuProps {
  documentId: string;
  label: string;
  tags: string[];
  /** All tags known in the library, for the edit-tags suggestions. */
  knownTags: string[];
}

/**
 * Per-document overflow menu: free-form tag editing and "Move to scope". Scopes
 * are tag-filters, so "moving" rewrites the document's tags — add the target
 * scope's own tags, drop the currently-viewed scope's. The Edit-tags dialog is
 * the precise fallback for anything the quick move can't express.
 */
export function DocumentActionsMenu({ documentId, label, tags, knownTags }: DocumentActionsMenuProps) {
  const t = useTranslations('documents');
  const scopes = useScopesStore((s) => s.scopes);
  const activeScopeId = useScopesStore((s) => s.activeScope);
  const updateScope = useScopesStore((s) => s.actions.updateScope);
  const getScopeTree = useScopesStore((s) => s.actions.getScopeTree);
  const updateDocumentTags = useDocumentsStore((s) => s.actions.updateDocumentTags);

  const [editOpen, setEditOpen] = React.useState(false);
  const [transferOpen, setTransferOpen] = React.useState(false);

  const activeScope = activeScopeId ? scopes.find((s) => s.scopeId === activeScopeId) : undefined;

  const moveTo = React.useCallback(
    (targetId: string) => {
      const target = scopes.find((s) => s.scopeId === targetId);
      if (!target) return;

      // A scope is defined by its tags. If the target has none of its own,
      // mint a slug from its name and persist it onto the scope so the scope
      // becomes a real filter and the document can actually join it (folder-like
      // assignment — see the tagless-scope design decision).
      let targetTags = target.tags;
      if (targetTags.length === 0) {
        const slug = slugifyTag(target.name);
        if (!slug) {
          toast.error(t('documentActionsMenu.deriveTagError', { name: target.name }), {
            description: t('documentActionsMenu.deriveTagErrorHint'),
          });
          return;
        }
        targetTags = [slug];
        updateScope(target.scopeId, target.name, target.description, targetTags, target.parentId, target.color);
        // Refresh the active scope's resolved tags so the list reflects the new
        // membership immediately if we're viewing the scope we just tagged.
        if (target.scopeId === activeScopeId) getScopeTree(target.scopeId);
      }

      const next = computeMovedTags(tags, targetTags, activeScope?.tags ?? []);
      updateDocumentTags(documentId, next);
      toast.success(t('documentActionsMenu.movedTo', { name: target.name }), { description: label });
    },
    [t, scopes, tags, activeScope, activeScopeId, updateScope, getScopeTree, updateDocumentTags, documentId, label],
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={t('documentActionsMenu.actionsFor', { label })}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-hover hover:text-foreground transition-colors duration-150"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[12rem] max-h-72 overflow-auto">
          <DropdownMenuItem className="flex items-center gap-2" onClick={() => setEditOpen(true)}>
            <Tag className="h-3.5 w-3.5" />
            {t('documentActionsMenu.editTags')}
          </DropdownMenuItem>

          <DropdownMenuItem className="flex items-center gap-2" onClick={() => setTransferOpen(true)}>
            <ArrowRightLeft className="h-3.5 w-3.5" />
            {t('documentActionsMenu.moveCopyToWorkspace')}
          </DropdownMenuItem>

          {scopes.length > 0 && (
            <>
              <div className="mt-1 flex items-center gap-1.5 px-3 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                <FolderInput className="h-3 w-3" /> {t('documentActionsMenu.moveToScope')}
              </div>
              {scopes.map((scope) => {
                const isCurrent = scope.scopeId === activeScopeId;
                return (
                  <DropdownMenuItem
                    key={scope.scopeId}
                    disabled={isCurrent}
                    className="flex items-center gap-2 pl-3"
                    onClick={() => moveTo(scope.scopeId)}
                  >
                    {scope.color && (
                      <span
                        className="inline-block h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: scope.color }}
                        aria-hidden
                      />
                    )}
                    <span className="truncate">{scope.name}</span>
                    {isCurrent && <span className="ml-auto text-[10px] text-muted-foreground">{t('documentActionsMenu.current')}</span>}
                  </DropdownMenuItem>
                );
              })}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <TagEditorDialog
        open={editOpen}
        label={label}
        initialTags={tags}
        knownTags={knownTags}
        onClose={() => setEditOpen(false)}
        onSave={(next) => updateDocumentTags(documentId, next)}
      />

      <TransferDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        item={{ kind: 'document', id: documentId, title: label }}
      />
    </>
  );
}
